<script setup lang="ts">
import { gamesUtils } from '@/compositions/gamesUtils'
import { securedQueryParams } from '@/compositions/onMountedApp'
import { utilsPlugin } from '@/plugins/utilsPlugin'
import { cartDrawerGaContext, cartDrawerGameKey, cartDrawerVisible, omitMinPurchaseMap } from './CartDrawerCompose'

const { windowWidth } = tools
const { routeGame, loading, showCheckoutCart, isLoggedGoat, logOut } = gamesUtils
const { gaRemoveFromCart, gaSections, gaViewCart } = gaUtils
const { t } = useI18n()

const gameKey = computed(() => {
  return routeGame.value?.gameKey || cartDrawerGameKey.value || ''
})

const gameCart = computed(() => {
  return useGameStore().gameCart(gameKey.value)
})
const loyalty = computed(() => useGameStore().loyaltyFor(gameKey.value))

function setQty(value: number | undefined, index: number, offerId: number | string | undefined) {
  if (`${value}` === '0') {
    removeFromCart(index, { gs_game: gameKey.value, gs_route: String(routeGame.value?.gameKey), gs_section: gaSections.cart })
  } else {
    useGameStore().updateGameCartOffer(gameKey.value, { qty: value, id: offerId } as Games.Offer, index)
  }
}
const drawerWidth = computed(() => {
  // return Math.min(windowWidth.value * 0.9, 500)
  return Math.min(windowWidth.value, 500)
})

if (!import.meta.env.SSR && securedQueryParams.value?.r_p_gameKey) {
  useGameStore().clearGameCart(securedQueryParams.value.r_p_gameKey || '')
  securedQueryParams.value.r_p_gameKey = ''
}

const isSsr = import.meta.env.SSR
let isMounted = false
function removeFromCart(index: number, context: GaUtils.GaContext) {
  const cartItem = gameCart.value[index]
  gaRemoveFromCart(
    {
      items: [{
        item_id: String(cartItem?.id),
        item_name: cartItem?.product || cartItem?.name,
        index,
        quantity: cartItem?.qty ?? 1,
        price: cartItem?.discount?.value || cartItem?.price?.value
      }],
      value: cartItem?.discount?.value || cartItem?.price?.value,
      currency: cartItem?.price?.currency,
      ...context,
      gs_context: `${isLoggedGoat.value ? gaUtils.gaBuyBtnContext.goat_account : gaUtils.gaBuyBtnContext.game_account}`
    }
  )
  useGameStore().removeFromGameCart(gameKey.value, index)
  if (gameCart.value.length === 0) {
    cartDrawerVisible.value = false
  }
}
function onOpenCart() {
  checkCartRewards()
  gaViewCart({
    ...(gameCart.value.length
      ? {
          currency: useGameStore().loyaltyFor(gameKey.value)?.cur,
          value: useGameStore().gameCartSumVal(gameKey.value) || 0,
          items: gameCart.value.map((cartItem, index) => {
            return {
              item_id: String(cartItem.id),
              item_name: cartItem?.product || cartItem?.name,
              index,
              quantity: cartItem?.qty ?? 1,
              price: cartItem?.discount?.value || cartItem?.price?.value
            }
          })
        }
      : { items: [] }),
    gs_game: cartDrawerGaContext.value?.gs_game || gameKey.value,
    gs_route: cartDrawerGaContext.value?.gs_route || String(utilsPlugin.getApp()?.$route?.name),
    gs_section: cartDrawerGaContext.value?.gs_section || gaSections.cart,
    gs_context: `${isLoggedGoat.value ? gaUtils.gaBuyBtnContext.goat_account : gaUtils.gaBuyBtnContext.game_account}`
  })
}
interface BonusCart {
  icon: string
  bonusAmount: number
  bonusAmountHuman: string
  title: string
  name: string
}
const bonusesRewards = ref<{ [key in Games.GameKeyType]?: BonusCart[] }>({})
const bonuses = computed<BonusCart[]>(() => {
  const map: Record<string, number> = {
    loyaltyPoints: 0
  }
  const bonusesList: BonusCart[] = [
    {
      icon: routeGame.value?.loyalty?.icon || '',
      bonusAmount: 0,
      bonusAmountHuman: '',
      title: 'coin',
      name: t('offerTileItems.loyaltyPoints')
    }
  ]
  const sumAmount = (idx: number | undefined, amount: number) => {
    if (idx === undefined)
      return
    const bonus = bonusesList[idx]
    if (bonus) {
      bonus.bonusAmount = (bonus.bonusAmount || 0) + (amount || 1)
    }
  }
  gameCart.value.forEach((cartItem) => {
    if (cartItem.loyaltyPoints && map.loyaltyPoints !== undefined) {
      sumAmount(map.loyaltyPoints, ((cartItem.loyaltyPoints || 0) * (cartItem.qty || 1)))
    }
    if (cartItem.items?.length) {
      cartItem.items.forEach((item) => {
        if (item.bonusAmount && (item.label || item.title) && item.asset) {
          const bonusKey = (item?.label || item?.title || '').toLowerCase()
          if (map?.[bonusKey] === undefined) {
            map[bonusKey] = bonusesList.push({
              icon: item.asset ? (item.asset.startsWith('http') ? item.asset : `${routeGame.value?.imgStorageUrl || ''}${item.asset}`) : '',
              bonusAmount: 0,
              bonusAmountHuman: '',
              title: item.title || '',
              name: item.label || item.title || ''
            }) - 1
          }
          sumAmount(map[bonusKey], (item.bonusAmount || 0) * (cartItem.qty || 1))
        }
      })
    }
  })
  bonusesRewards.value?.[routeGame.value?.gameKey || '']?.forEach((reward) => {
    const bonusKey = (reward?.name || '').toLowerCase()
    if (map?.[bonusKey] !== undefined) {
      sumAmount(map[bonusKey], (reward.bonusAmount || 0))
    } else {
      bonusesList.push(reward)
    }
  })
  bonusesList.forEach((bonusItem) => {
    bonusItem.bonusAmountHuman = gamesUtils.numbersShortHuman(bonusItem.bonusAmount || 0)
  })
  return bonusesList.filter(item => !!item.bonusAmount).sort((a, b) => (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase()))
})
const cartLoyaltyPoints = computed(() => {
  let points = 0
  bonuses.value.some((item) => {
    if (item.title === 'coin') {
      points = item.bonusAmount || 0
      return true
    }
    return false
  })
  return points
})
const loadingRewards = ref<{ [key in Games.GameKeyType]?: boolean }>({ [routeGame.value?.gameKey || '']: true })
function checkCartRewards() {
  if (cartLoyaltyPoints.value && isMounted) {
    utilsPlugin.nextLoopEvent(100).then(() => {
      loadingRewards.value[routeGame.value?.gameKey || ''] = true
      gamesUtils
        .apiGet(gameKey.value, 'getbasketrewards/', { loyalty_points: cartLoyaltyPoints.value })
        .then(({ data }) => {
          if (!isMounted) {
            return
          }
          bonusesRewards.value[routeGame.value?.gameKey || ''] = []
          if (data?.loyaltyRewardsMergedBasket?.items?.length) {
            data.loyaltyRewardsMergedBasket.items.forEach((item: Games.OfferItemDescription) => {
              bonusesRewards.value[routeGame.value?.gameKey || '']?.push({
                icon: item.asset ? (item.asset.startsWith('http') ? item.asset : `${routeGame.value?.imgStorageUrl || ''}${item.asset}`) : '',
                bonusAmount: item?.amount || 0,
                bonusAmountHuman: gamesUtils.numbersShortHuman(item?.amount || 0),
                title: item.title || '',
                name: item?.label || item?.title || ''
              })
            })
          }
        })
        .catch((err) => {
          if (err?.response?.status === 401 || err?.status === 401) {
            logOut(gameKey.value, err)
          } else {
            gamesUtils.catchError(err)
          }
        })
        .then(() => {
          loadingRewards.value[routeGame.value?.gameKey || ''] = false
        })
    })
  } else {
    bonusesRewards.value[routeGame.value?.gameKey || ''] = []
  }
}
function onClose() {
  loadingRewards.value[routeGame.value?.gameKey || ''] = true
  loading.value = ''
}
const isPaymentAvailable = computed(() => {
  if (gameCart.value.length && omitMinPurchaseMap.value?.[gameKey.value]?.length) {
    let isOmitOffer = false
    gameCart.value.some((offer) => {
      if (omitMinPurchaseMap.value?.[gameKey.value]?.includes(Number.parseInt(`${offer.id || 0}`))) {
        isOmitOffer = true
        return true
      }
      return false
    })
    if (isOmitOffer) {
      return true
    }
  }
  return useGameStore().gameCartPaymentAvailable(gameKey.value) || false
})
const cartSumValHuman = computed(() => useGameStore().gameCartSumValHuman(gameKey.value))
onMounted(() => {
  isMounted = true
  watch(cartSumValHuman, () => {
    checkCartRewards()
  })
})
onUnmounted(() => {
  isMounted = false
})
</script>

<template>
  <el-drawer
    v-if="!isSsr"
    v-model="cartDrawerVisible"
    :size="`${drawerWidth}px`"
    modal-class="cart-drawer"
    @closed="onClose"
    @opened="onOpenCart"
  >
    <template #header>
      <div class="w-full flex justify-start items-center gap-x-2 text-white">
        <div>
          <icon-ify
            icon="tdesign:cart"
            class="inline text-2xl"
          />
        </div>
        <div>{{ t('cartDrawer.itemCount', gameCart.length) }}</div>
      </div>
    </template>
    <template #default>
      <div class="flex flex-col gap-y-4">
        <div
          v-for="(offer, index) in gameCart"
          :key="offer.id"
        >
          <div class="flex flex-row gap-1 items-center bg-white rounded-xl p-2">
            <div class="w-3/12 pr-2">
              <img
                v-if="offer.asset"
                :src="offer.asset"
                loading="lazy"
                :alt="t('cartDrawer.offerImgAlt')"
                class="rounded-md"
              >
            </div>
            <div class="w-9/12 relative">
              <div class="font-bold truncate">
                {{ offer.name || `Offer_${offer.id}` }}
              </div>
              <div class="flex flex-row flex-wrap gap-2 big:gap-x-3 text-sm text-neutral-600 justify-between">
                <div>{{ offer?.discount?.format || offer?.price?.format }}</div>
                <div class="whitespace-nowrap">
                  <el-input-number
                    :model-value="offer.qty"
                    :step="1"
                    step-strictly
                    :min="0"
                    :max="offer.allowQty ? (offer.purchaseLimit || 1) : 1"
                    size="small"
                    class="w-16 ml-1"
                    @update:model-value="setQty($event, index, offer?.id)"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <template
        v-if="bonuses.length && !loadingRewards?.[routeGame?.gameKey || '']"
      >
        <div
          class="font-bold text-sm text-white my-5 pl-1"
        >
          {{ t('cartDrawer.bonusesTitle') }}
        </div>
        <div
          v-if="routeGame?.gameKey"
          class="text-white ml-3 grid grid-cols-[auto_1fr] gap-y-3 gap-x-5 items-center"
        >
          <template
            v-for="(bonus, index) in bonuses"
            :key="index"
          >
            <div class="item-box bg-white w-11 h-11 mini:w-12 mini:h-12 rounded-lg border-2 border-white shadow-sm relative">
              <div class="absolute left-1.5 right-1.5 bottom-1.5 top-1">
                <template v-if="bonus.icon">
                  <img
                    :alt="t('offerTileItems.itemAlt')"
                    :src="bonus.icon"
                    class="block max-w-9 mini:max-w-11 big:max-w-9 mx-auto max-h-full"
                  >
                </template>
              </div>
              <div
                v-if="bonus.bonusAmountHuman"
                class="text-center font-semibold leading-none p-0.5 whitespace-nowrap z-10 text-[0.8rem] mini:text-[0.95rem] big:text-[0.8rem] lg:text-[0.9rem]"
              >
                <div
                  class="gs-text-shadow font-black text-white text-center absolute left-1/2 -translate-x-1/2 bottom-0.5"
                  :class="{ 'tracking-tighter': bonus.bonusAmountHuman.length > 4 }"
                >
                  {{ bonus.bonusAmountHuman }}
                </div>
              </div>
            </div>
            <div class="font-bold text-sm">
              x<span class="capitalize">{{ bonus.bonusAmountHuman }} {{ bonus.name }}</span>
            </div>
          </template>
        </div>
      </template>
    </template>
    <template #footer>
      <div
        class="text-white grid grid-cols-[1fr_auto] gap-1 items-center bg-[#0a343b] pl-5 pr-4 py-6 relative"
      >
        <div class="text-left text-[1.5rem]">
          <div
            class="text-[0.8rem] font-bold"
          >
            {{ t('cartDrawer.total') }}
          </div>
          <div class="">
            <template v-if="cartSumValHuman">
              {{ cartSumValHuman }}
            </template>
            <template v-else>
              {{ loyalty?.cur }} 0.00
            </template>
          </div>
          <div
            v-if="!isPaymentAvailable"
            class="absolute bottom-1.5 left-0 right-0 text-center text-xs text-white"
          >
            {{ t('cartDrawer.minCartValuePre') }} <b>{{ loyalty?.minPurchaseCurHuman }}</b>{{ t('cartDrawer.minCartValuePost') }}
          </div>
        </div>
        <el-button
          color="#38a6bb"
          :disabled="!gameCart?.length || !isPaymentAvailable"
          class="text-white uppercase font-black rounded-lg tracking-wide text-base big:text-lg big:px-5 h-10"
          size="large"
          :loading="loading === 'cart'"
          @click="showCheckoutCart(gaSections.cart, gameKey)"
        >
          {{ t('cartDrawer.checkout') }}
        </el-button>
      </div>
      <div
        class="h-10 grid grid-cols-1 items-center text-center text-xs  text-white"
      />
    </template>
  </el-drawer>
</template>

<style lang="postcss">
.cart-drawer {
  .el-drawer {
    --el-drawer-bg-color: #051A1E;
    --el-drawer-padding-primary: 8px;
  }

  .el-drawer__header {
    margin-bottom: 0;
    color: white;
    padding: 16px;
  }

  .el-drawer__footer {
    padding: 0;
  }

  .el-input {
        --el-border-color: black;
        --el-input-focus-border-color: black;
  }

  .el-input__wrapper {
    background: #f7f7f7;
    padding-left: 25px !important;
    padding-right: 25px !important;
  }

  .el-input-number {
    --el-fill-color-light: #f7f7f7;
    --el-text-color-regular: black;
    --el-border: none;

    &:hover {
      --el-border: none;
    }

    .el-input-number__decrease, .el-input-number__increase {
      background: #f7f7f7;

      &:hover {
        color: black;
      }

      .el-icon {
        transform: scale(1.2);
      }

      &.is-disabled {
        opacity: 0.3;
      }
    }
  }

  .el-input-number__inner {
    background: #f7f7f7;
  }

  .el-button {
    --el-button-disabled-bg-color: #BBB !important;
  }
}
</style>
